import React from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleQuestion,
  faHandshake,
  faMotorcycle,
  faShieldHalved,
  faWallet,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { usePublicContact } from "../../context/usePublicContactContext"

const courierFaq = ["faq_payment_origin", "faq_split", "faq_when_receive", "faq_support"] as const

export const CourierHelpCenterPage: React.FC = () => {
  const { t } = useTranslation("courierHelp")

  const { contact } = usePublicContact()

  return (
    <div className="min-h-screen space-y-6">
      <Animate type={AnimationTypes.fadeInTraslate}>
        <section className="rounded-xl border-2 border-red-100 bg-gradient-to-r from-red-50 to-orange-50 p-4 shadow-sm">
          <span className="inline-flex items-center gap-2 rounded-full bg-red-600 px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white">
            <FontAwesomeIcon icon={faCircleQuestion} />
            {t("label_badge")}
          </span>

          <h1 className="mt-4 text-xl font-black text-slate-900 sm:text-3xl">{t("label_title")}</h1>

          <p className="mt-2 text-sm text-slate-600 sm:text-base">{t("label_description")}</p>
        </section>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
        <section className="rounded-xl border-2 border-red-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-bold text-red-700">{t("label_disclaimer_title")}</p>

          <p className="mt-2 text-sm leading-relaxed text-slate-700">{t("label_disclaimer_text")}</p>
        </section>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
        <section className="grid gap-4 lg:grid-cols-3">
          {[
            [faWallet, "card_money_title", "card_money_description", "bg-orange-100 text-orange-600"],

            [faMotorcycle, "card_courier_title", "card_courier_description", "bg-red-100 text-red-600"],

            [faShieldHalved, "card_security_title", "card_security_description", "bg-orange-100 text-orange-700"],
          ].map(([icon, title, description, className]) => (
            <article key={String(title)} className="rounded-xl border-2 border-orange-100 bg-white p-4 shadow-sm">
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${className}`}>
                <FontAwesomeIcon icon={icon as any} />
              </span>

              <h2 className="mt-3 text-base font-extrabold text-slate-900">{t(title as any)}</h2>

              <p className="mt-1 text-sm text-slate-600">{t(description as any)}</p>
            </article>
          ))}
        </section>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="2.2s">
        <section className="rounded-xl border-2 border-red-100 bg-white p-4 shadow-sm">
          <h3 className="text-lg font-extrabold text-slate-900">{t("label_faq_title")}</h3>

          <div className="mt-4 space-y-3">
            {courierFaq.map((item) => (
              <details
                key={item}
                className="group rounded-md border-2 border-orange-100 bg-orange-50/40 px-4 py-3 transition-colors open:border-red-200 open:bg-white"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-slate-800">
                  {t(`${item}.question`)}
                  <span className="text-red-500 transition-transform group-open:rotate-45">+</span>
                </summary>

                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(`${item}.answer`)}</p>
              </details>
            ))}
          </div>
        </section>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="2.6s">
        <section className="rounded-2xl border-2 border-orange-100 bg-white p-4 shadow-sm">
          <h3 className="text-lg font-extrabold text-slate-900">{t("label_links_title")}</h3>

          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-md border-2 border-orange-100 bg-orange-50/40 px-4 py-3 text-sm font-semibold text-slate-800">
              <span>{t("label_link_docs")}</span>

              <FontAwesomeIcon icon={faHandshake} className="text-red-500" />
            </div>

            <div className="flex items-center justify-between rounded-md border-2 border-orange-100 bg-orange-50/40 px-4 py-3 text-sm font-semibold text-slate-800">
              <span>{t("label_link_help")}</span>

              <FontAwesomeIcon icon={faHandshake} className="text-red-500" />
            </div>
          </div>

          {contact.email && (
            <p className="mt-4 text-sm text-slate-700">
              {t("label_contact_text")}: {contact.email}
            </p>
          )}
        </section>
      </Animate>
    </div>
  )
}
